import { createContext, useContext } from "solid-js";
import { createSignal, Accessor, Setter } from "solid-js";
import { clientRepo } from "~/server/apis/client_repo";
import { showSnackBar } from "~/shared_states/snackbar";
import { useNavigate } from "@solidjs/router";
import { BrandCardProps } from "./brands_card";
import { useModal } from "~/components/modal";
import { ConfirmationDialog } from "~/components/brand_l2/confirmation_dialog";
import { toRupees } from "~/utils/number";
import { useWebStoreContext } from "~/context/webstore_context";
import useAxisUserInfoModal from "./hooks/useAxisUserInfoModal";
import { ProfileUpdateParams } from "./fragments/axis_user_info_modal";
interface HomeV2ContextType {
  isRedemptionInProgress: Accessor<boolean>;
  redeem: (amount: number, productId: string) => Promise<any>;
  onClickBrandCard: (
    brand: BrandCardProps,
    noConfirmationNeeded?: boolean
  ) => Promise<void>;
}

const HomeV2Context = createContext<HomeV2ContextType>();

export function HomeV2Provider(props: { children: any }) {
  const { storeState, refreshV4HomePage } = useWebStoreContext();
  const [isRedemptionInProgress, setIsRedemptionInProgress] =
    createSignal(false);

  const { setIsModalOn, updateModalContentGetter, setOuterFrameClass } =
    useModal()!;

  const navigate = useNavigate();
  const axisUserInfoModal = useAxisUserInfoModal();

  async function redeem(amount: number, productId: string) {
    setIsRedemptionInProgress(true);
    try {
      return await clientRepo.initiateRedemption({
        amount: amount,
        paymentMode: "UPI",
        productId: productId,
        consumeCoins: true,
        denominationDetails: [
          {
            denomination: amount,
            quantity: 1,
          },
        ],
      });
    } finally {
      setIsRedemptionInProgress(false);
    }
  }

  /**
   * Check if user has complete profile data (name, email, phone)
   */
  function isUserDataComplete(): boolean {
    const user = storeState.v4HomePage?.user;
    if (!user) return false;

    const hasName = !!user.firstName;
    const hasEmail = !!user.email;
    const hasPhone = !!user.phoneNumber;

    return hasName && hasEmail && hasPhone;
  }

  /**
   * Get full user name from user object
   */
  function getFullUserName(): string {
    const user = storeState.v4HomePage?.user;
    if (!user) return "";
    return `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim();
  }

  async function onClickBrandCard(
    brand: BrandCardProps,
    noConfirmationNeeded?: boolean
  ) {
    let redemptionAmount = storeState.coins ?? 0;
    const onConfirm = async () => {
      try {
        let orderResponse = await redeem(redemptionAmount, brand.brandId);
        navigate(
          `/buy-gift-card/${brand.brandId}/order?orderId=${orderResponse.storeOrderDetails.id}`
        );
      } catch (error: any) {
        showSnackBar({
          message: error.message,
          level: "error",
        });
      }
    };

    if (isRedemptionInProgress()) {
      return;
    }

    if (storeState.coins === 0) {
      showSnackBar({
        message:
          "Already redeemed. Check 'My gift cards' in the menu for details.",
        level: "error",
      });
      return;
    }

    // Check if user data is complete before proceeding
    if (!isUserDataComplete()) {
      const user = storeState.v4HomePage?.user;
      axisUserInfoModal.openModal({
        email: user?.email ?? null,
        phone: user?.phoneNumber ?? null,
        name: getFullUserName() || null,
        redemptionAmount: redemptionAmount,
        brandName: brand.title,
        brandLogoUrl: brand.logoUrl,
        onSubmit: async (profileParams: ProfileUpdateParams) => {
          await handleProfileUpdate(profileParams, brand, noConfirmationNeeded);
        },
      });
      return;
    }

    // User data is complete, proceed with normal flow
    if (noConfirmationNeeded) {
      await onConfirm();
    } else {
      showConfirmationDialog({
        amount: redemptionAmount,
        brandName: brand.title,
        logoUrl: brand.logoUrl,
        onConfirm: onConfirm,
      });
    }
  }

  /**
   * Handle profile update submission from user info modal
   */
  async function handleProfileUpdate(
    profileParams: ProfileUpdateParams,
    brand: BrandCardProps,
    noConfirmationNeeded?: boolean
  ) {
    axisUserInfoModal.setShowLoader(true);

    try {
      // Update user profile via API
      await clientRepo.updatePartnerUserProfile(profileParams);

      // Refresh homepage data to get updated user info
      await refreshV4HomePage();

      // Close the user info modal
      await axisUserInfoModal.closeModal();

      // Continue with brand card click flow (now with complete data)
      await onClickBrandCard(brand, noConfirmationNeeded);
    } catch (error: any) {
      showSnackBar({
        message: error.message ?? "Failed to update profile. Please try again.",
        level: "error",
      });
    } finally {
      axisUserInfoModal.setShowLoader(false);
    }
  }

  function showConfirmationDialog(props: {
    amount: number;
    brandName: string;
    logoUrl: string;
    onConfirm: () => Promise<void>;
  }) {
    const getBrandName = () => {
      if (props.brandName.toLowerCase().includes("amazon")) {
        return "Amazon";
      }
      return props.brandName;
    };

    updateModalContentGetter(() => {
      return (
        <ConfirmationDialog
          title={`${toRupees(props.amount)} ${getBrandName()} card`}
          message={
            <div class="flex flex-row items-center text-normal text-textDark ">
              {`Proceed to generate ${getBrandName()} gift card`}
            </div>
          }
          logoUrl={props.logoUrl}
          isLoading={isRedemptionInProgress()}
          onConfirm={async () => {
            await props.onConfirm();
            setIsModalOn(false);
            setOuterFrameClass("");
          }}
          onCancel={() => {
            setIsModalOn(false);
            setOuterFrameClass("");
          }}
        />
      );
    });
    setIsModalOn(true);
  }

  const value = {
    isRedemptionInProgress,
    redeem,
    onClickBrandCard,
  };

  return (
    <HomeV2Context.Provider value={value}>
      {props.children}
    </HomeV2Context.Provider>
  );
}

export function useHomeV2Context() {
  const context = useContext(HomeV2Context);
  if (!context) {
    throw new Error("useHomeV2Context must be used within a HomeV2Provider");
  }
  return context;
}
